import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';

import { ExperimentFrequencyTableComponent } from './experiment-frequency-table.component';
import { ExperimentResultsTableComponent } from './experiment-results-table.component';
import { OutcomeSvgComponent } from './outcome-svg.component';
import { ExperimentTrialResult, ProbabilityExperimentDefinition } from './probability-experiment.model';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html', trust: true });
}

@Component({
  selector: 'dojo-experiment-run-detail',
  standalone: true,
  imports: [OutcomeSvgComponent, ExperimentResultsTableComponent, ExperimentFrequencyTableComponent],
  templateUrl: './experiment-run-detail.component.html',
  styleUrl: './experiment-run-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class ExperimentRunDetailComponent {
  constructor(private readonly sanitizer: DomSanitizer) {}

  readonly results = input.required<ExperimentTrialResult[]>();
  readonly experiment = input.required<ProbabilityExperimentDefinition>();
  readonly targetOutcomes = input.required<boolean[]>();
  readonly successProbability = input.required<number>();

  readonly highlightType = signal<'success' | 'loss' | null>(null);

  readonly nHtml = renderLatex('n');

  readonly summaryStats = computed(() => {
    const results = this.results();
    const total = results.length;
    const successCount = results.filter((r) => r.isSuccess).length;
    const lossCount = total - successCount;
    return {
      total,
      successCount,
      lossCount,
      successProb: total > 0 ? successCount / total : 0,
      lossProb: total > 0 ? lossCount / total : 0,
    };
  });

  readonly empiricalSuccessHtml = computed<SafeHtml>(() => {
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P(A) = ${this.summaryStats().successProb.toFixed(2)}`));
  });

  readonly empiricalLossHtml = computed<SafeHtml>(() => {
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P(A') = ${this.summaryStats().lossProb.toFixed(2)}`));
  });

  readonly successSvgTypes = computed(() => {
    const exp = this.experiment();
    const selected = this.targetOutcomes();
    return exp.outcomes.filter((_, i) => selected[i]).map((o) => o.svgType);
  });

  readonly lossSvgTypes = computed(() => {
    const exp = this.experiment();
    const selected = this.targetOutcomes();
    return exp.outcomes.filter((_, i) => !selected[i]).map((o) => o.svgType);
  });
}
